import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Button, Typography, useTheme } from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import { useTranslation } from "../context/I18nContext";

interface ImageSelectorProps {
  selectedImage: string | null;
  setSelectedImage: React.Dispatch<React.SetStateAction<string | null>>;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  disabled?: boolean;
}

const ImageSelector: React.FC<ImageSelectorProps> = ({
  selectedImage,
  setSelectedImage,
  selectedFile,
  setSelectedFile,
  disabled,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (selectedImage) URL.revokeObjectURL(selectedImage);
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setSelectedImage(URL.createObjectURL(file));
      }
    },
    [selectedImage, selectedFile],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    disabled,
  });

  const defaultBorderColor = isDragActive
    ? theme.palette.primary.main
    : theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(25,28,30,0.14)";

  if (selectedImage) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          py: 3,
        }}
      >
        <Box
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            maxWidth: 320,
            boxShadow: "0 20px 40px rgba(25,28,30,0.12)",
          }}
        >
          <img
            src={selectedImage}
            alt="Selected"
            style={{ maxWidth: "100%", maxHeight: 320, display: "block" }}
          />
        </Box>
        {!disabled && (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              URL.revokeObjectURL(selectedImage);
              setSelectedImage(null);
              setSelectedFile(null);
            }}
            sx={{
              mt: 1.5,
              color: "text.secondary",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {t("home.select_file")}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      {...getRootProps()}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        p: { xs: 4, sm: 6 },
        minHeight: 220,
        cursor: disabled ? "default" : "pointer",
        bgcolor: "transparent",
        borderRadius: 2,
        "&:hover .dropzone-border rect": disabled
          ? {}
          : {
              stroke: theme.palette.primary.light,
            },
      }}
    >
      <svg
        className="dropzone-border"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <rect
          x="1"
          y="1"
          rx="8"
          ry="8"
          fill="none"
          stroke={defaultBorderColor}
          strokeWidth="2"
          strokeDasharray="6 6"
          style={{
            width: "calc(100% - 2px)",
            height: "calc(100% - 2px)",
            transition: "stroke 0.2s ease",
          }}
        />
      </svg>
      {!disabled && <input {...getInputProps()} />}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,65,82,0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        }}
      >
        <CameraAltOutlinedIcon sx={{ fontSize: 28 }} />
      </Box>
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}
        >
          {isDragActive
            ? t("home.drop_here")
            : disabled
              ? t("home.login_required")
              : t("home.drag_drop")}
        </Typography>
        {!disabled && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem" }}
          >
            {t("home.file_formats")}
          </Typography>
        )}
      </Box>
      {!disabled && (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
          sx={{
            mt: 1,
            px: 3,
            borderRadius: 1,
            fontSize: "0.7rem",
            letterSpacing: "0.08em",
            fontWeight: 700,
            textTransform: "uppercase",
            color: "text.primary",
            borderColor: theme.palette.divider,
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "transparent",
            },
          }}
        >
          {t("home.select_file")}
        </Button>
      )}
    </Box>
  );
};

export default ImageSelector;
